import { ActionFactoryParams } from '../../../../utils/ControlledComponent/ControlledComponent.types';
import { CalendarContext } from '../../../../utils/context/contextFactory';
import { WidgetElements } from '../../../../utils/bi/consts';
import { CalendarState } from '../../controller';
import { CloseDialogAction } from '../closeDialog/closeDialog';
import { mapDialogTypeToWidgetComponent } from '../../../../utils/bi/mappers';
import { bookingsCalendarClick } from '@wix/bi-logger-wixboost-ugc/v2';
import { DialogType } from '../../ViewModel/dialogViewModel/dialogViewModel';
import {
  storeCalendarSelections,
  BookingsQueryParams,
  CalendarSelections,
} from '@wix/bookings-catalog-calendar-viewer-utils';
import {
  getDefaultStaffMemberId,
  getDefaultLocation,
  getDefaultCustomVariant,
} from '@wix/bookings-services-preferences-modal/helpers';
import { mapPresetToWidgetName } from '../../../../utils/presets';

export type DialogCloseElement =
  | WidgetElements.DIALOG_X_BUTTON
  | WidgetElements.DIALOG_CLOSE_BUTTON;

export type OnDialogClose = (clickedElement?: DialogCloseElement) => void;

export function createOnDialogCloseAction(
  {
    getControllerState,
    context,
  }: ActionFactoryParams<CalendarState, CalendarContext>,
  closeDialog: CloseDialogAction,
): OnDialogClose {
  return (clickedElement = WidgetElements.DIALOG_X_BUTTON) => {
    const [state] = getControllerState();
    const { dialog } = state;

    void context.biLogger.report(
      bookingsCalendarClick({
        component: mapDialogTypeToWidgetComponent(dialog?.type),
        element: clickedElement,
      }),
    );

    if (
      dialog?.type === DialogType.ServicesPreferences &&
      context.experiments.enabled('specs.bookings.SOA.A1DefaultsValues')
    ) {
      try {
        handleCloseOnServicesPreferences(context, state, closeDialog);
        return;
      } catch (e) {}
    }
    closeDialog();
  };
}

const handleCloseOnServicesPreferences = (
  context: CalendarContext,
  state: CalendarState,
  closeDialog: CloseDialogAction,
) => {
  const {
    bookingsSettings,
    experiments,
    preset,
    flowAPI,
    reloadWidget,
    calendarSelections,
    isDateAndTimeViewMode,
  } = context;

  if (
    isDateAndTimeViewMode ||
    !!calendarSelections?.services?.length ||
    !!calendarSelections?.location
  ) {
    closeDialog();
    return;
  }

  const servicesInView = state.servicesInView || [];
  const openedBy = mapPresetToWidgetName(preset);

  const firstService = servicesInView[0];
  if (!firstService) {
    closeDialog();
    return;
  }
  const serviceVariantsMap = state.serviceVariantsMap || {};
  const queryParams = flowAPI.controllerConfig.wixCodeApi.location.query || {};
  const defaultLocation = firstService
    ? getDefaultLocation({
        service: firstService,
        bookingsSettings,
        experiments,
        serviceOptionsAndVariants: serviceVariantsMap[firstService.id!],
        openedBy,
        queryParams,
      })
    : undefined;

  const defaultStaff = getDefaultStaffMemberId({
    service: firstService,
    bookingsSettings,
    experiments,
    serviceOptionsAndVariants: serviceVariantsMap[firstService.id!],
    openedBy,
    queryParams,
  });

  const defaultVariant = getDefaultCustomVariant({
    serviceOptionsAndVariants: serviceVariantsMap[firstService.id!],
    service: firstService,
    bookingsSettings,
    experiments,
    openedBy,
    queryParams,
  });

  const defaultCalendarSelections: CalendarSelections = {
    services: [
      {
        id: firstService.id!,
        resources: defaultStaff ? [defaultStaff] : [],
        ...(defaultVariant ? { variants: [defaultVariant] } : {}),
      },
    ],
    location: defaultLocation?.value,
  };

  const session = flowAPI.controllerConfig.platformAPIs.storage.session;
  storeCalendarSelections({
    calendarSelections: defaultCalendarSelections,
    session,
  });

  const isRemoveSkipPreferencesModalParamEnabled = experiments.enabled(
    'specs.bookings.removeSkipPreferencesModalParam',
  );

  closeDialog();
  if (!isRemoveSkipPreferencesModalParamEnabled) {
    flowAPI.controllerConfig.wixCodeApi.location.queryParams.add({
      [BookingsQueryParams.SKIP_PREFERENCES_MODAL]: 'true',
    });
  }
  reloadWidget();
};
